import React, { useMemo, useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';
import { BuiltLogoMark } from './ui/BuiltLogoMark';

type NavItem = { name: string; href: string };
type NavGroup = { id: string; label: string; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [{ name: 'Platform overview', href: '/admin' }],
  },
  {
    id: 'community',
    label: 'Community',
    items: [
      { name: 'Users', href: '/admin/users' },
      { name: 'Clubs', href: '/admin/clubs' },
      { name: 'Shops', href: '/admin/shops' },
      { name: 'Certifications', href: '/admin/certifications' },
      { name: 'Badges', href: '/admin/badges' },
      { name: 'Expansion', href: '/admin/expansion' },
    ],
  },
  {
    id: 'events',
    label: 'Events & payments',
    items: [
      { name: 'Events', href: '/admin/events' },
      { name: 'Payments', href: '/admin/payments' },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    items: [
      { name: 'Featured Builds', href: '/admin/featured' },
      { name: 'Sponsorship', href: '/admin/sponsorship' },
      { name: 'Achievements', href: '/admin/achievements' },
      { name: 'Product updates', href: '/admin/updates' },
      { name: 'Testimonials', href: '/admin/testimonials' },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    items: [
      { name: 'Invite codes', href: '/admin/codes' },
      { name: 'AI settings', href: '/admin/ai' },
      { name: 'Activity log', href: '/admin/activity' },
    ],
  },
];

function envBadge(): { label: string; tone: string } {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    return { label: 'Local', tone: 'bg-sky-500/15 text-sky-300 border-sky-500/30' };
  }
  if (host.startsWith('staging.')) {
    return { label: 'Staging', tone: 'bg-amber-500/15 text-amber-200 border-amber-500/30' };
  }
  if (host.includes('biltspec')) {
    return { label: 'Production', tone: 'bg-rose-500/15 text-rose-200 border-rose-500/30' };
  }
  return { label: 'Admin', tone: 'bg-white/10 text-gray-300 border-white/20' };
}

export const AdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const badge = useMemo(() => envBadge(), []);

  const isActive = (href: string) =>
    href === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(href);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const nav = (
    <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto" aria-label="Admin">
      {NAV_GROUPS.map((group) => {
        const isCollapsed = !!collapsed[group.id];
        return (
          <div key={group.id}>
            <button
              type="button"
              className="flex w-full items-center justify-between px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 cursor-pointer"
              onClick={() => setCollapsed((c) => ({ ...c, [group.id]: !c[group.id] }))}
              aria-expanded={!isCollapsed}
            >
              <span>{group.label}</span>
              <span aria-hidden="true" className="text-gray-600">
                {isCollapsed ? '+' : '−'}
              </span>
            </button>
            {!isCollapsed && (
              <ul className="mt-1 space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[40px] ${
                          active
                            ? 'bg-orange-500/[0.1] text-orange-300 border border-orange-500/30'
                            : 'text-gray-300 border border-transparent hover:bg-white/[0.04] hover:text-white'
                        }`}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="truncate">{item.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen overflow-x-hidden flex flex-col bg-[#0a0a0a]">
      <div className="lg:hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06] bg-[#0c0c0c]">
          <div className="flex items-center min-w-0 flex-1 gap-3">
            <BuiltLogoMark size="sm" />
            <div className="min-w-0">
              <h1 className="font-display text-lg font-semibold tracking-tight text-white truncate">Admin</h1>
              <span className={`inline-flex mt-0.5 px-1.5 py-0.5 rounded border text-[10px] font-medium ${badge.tone}`}>
                {badge.label}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-gray-400 hover:text-white p-2 flex-shrink-0 min-h-[44px] min-w-[44px] cursor-pointer"
            aria-expanded={isMobileMenuOpen}
            aria-controls="admin-mobile-nav"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex flex-1">
        <aside
          id="admin-mobile-nav"
          className={`${isMobileMenuOpen ? 'block' : 'hidden'} lg:block fixed lg:static inset-y-0 left-0 z-50 w-64 border-r border-white/[0.06] lg:min-h-screen bg-[#0c0c0c]`}
        >
          <div className="flex flex-col h-full">
            <div className="hidden lg:flex items-center justify-between px-4 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-3 min-w-0">
                <BuiltLogoMark size="sm" />
                <div className="min-w-0">
                  <h1 className="font-display text-lg font-semibold tracking-tight text-white">Admin</h1>
                  <span className={`inline-flex mt-0.5 px-1.5 py-0.5 rounded border text-[10px] font-medium ${badge.tone}`}>
                    {badge.label}
                  </span>
                </div>
              </div>
            </div>

            {nav}

            <div className="border-t border-white/[0.06] p-4">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gradient-brand-tile rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-medium">
                    {user?.username?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{user?.username}</p>
                  <p className="text-xs text-gray-500">Super Admin</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Link
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:bg-white/[0.04] hover:text-white rounded-lg transition-colors min-h-[40px]"
                >
                  Back to Bilt
                </Link>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-300 hover:bg-white/[0.04] hover:text-white rounded-lg transition-colors min-h-[40px] cursor-pointer"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </aside>

        {isMobileMenuOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-black/50 z-40 lg:hidden cursor-pointer"
            aria-label="Close menu overlay"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        <div className="flex-1 lg:ml-0 min-w-0 overflow-x-hidden">
          <main className="p-4 sm:p-6 max-w-[1400px]">
            <Outlet />
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};
